import { t } from "@lingui/macro";
import { useState } from "react";
import { useResumeStore } from "../../stores/resume";
import { aiApiService } from "../../services/smart-ai/aiApiService";
import { useToast } from "../../hooks/use-toast";

export const useResumeRefinement = () => {
  const [isRefining, setIsRefining] = useState(false);
  const { toast } = useToast();
  const setValue = useResumeStore((state) => state.setValue);
  const resume = useResumeStore((state) => state.resume);

  const smartFix = async (suggestionTitle: string, suggestionText: string) => {
    setIsRefining(true);

    try {
      // Determine which section to refine based on the suggestion title/content
      let sectionName = "experience"; // Default
      const lowerTitle = suggestionTitle.toLowerCase();
      const lowerText = suggestionText.toLowerCase();

      if (lowerTitle.includes("summary") || lowerText.includes("summary")) {
        sectionName = "summary";
      } else if (lowerTitle.includes("skill") || lowerText.includes("skill")) {
        sectionName = "skills";
      }
      
      const sectionData = resume.data.sections[sectionName as keyof typeof resume.data.sections];
      const targetRole = resume.data.basics.headline || "Software Engineer";

      toast({
        title: t`Fixing...`,
        description: t`AI is optimizing your ${sectionName} section.`,
      });

      const refinedData = await aiApiService.refineResumeSection(
        sectionName,
        sectionData,
        suggestionText,
        targetRole
      );

      if (refinedData) {
        // Update the store with refined data
        setValue(`sections.${sectionName}`, refinedData);
        
        toast({
          title: t`Fixed!`,
          description: t`Your ${sectionName} has been optimized.`,
          variant: "default",
        });
        return true;
      } else {
        throw new Error("Failed to refine section");
      }
    } catch (error) {
      console.error("Smart Fix Error:", error);
      toast({
        title: t`Fix Failed`,
        description: t`Could not apply the smart fix. Please try again.`,
        variant: "error",
      });
      return false;
    } finally {
      setIsRefining(false);
    }
  };

  return {
    isRefining,
    smartFix,
  };
};
